import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiActivity, FiTrendingUp, FiTrendingDown, FiUsers, FiDollarSign,
  FiClock, FiZap, FiHash, FiCheckCircle, FiXCircle, FiRefreshCw,
  FiTarget, FiEye, FiLayers, FiPlus,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import ThreadComposer from './ThreadComposer';
import ContentQueueManager from './ContentQueueManager';

/**
 * Twitter Growth Panel — the closed-loop dashboard for the reply agent,
 * follow-up engine, viral tweet finder, and thread scheduler.
 *
 * Everything on this screen comes from /api/agent-analytics in ONE fetch.
 * The server aggregates against views (reply_agent_daily_rollup, etc.),
 * so the browser never touches raw tables and RLS filters admin-only.
 */

// Tailwind-safe literal palette so JIT never purges (see GrowthMetricsPanel).
const ACCENT: Record<string, { text: string; bg: string; bar: string; border: string; grad: string }> = {
  cyan:    { text: 'text-cyan-400',    bg: 'bg-cyan-500/10',    bar: 'bg-cyan-400',    border: 'border-cyan-500/30',    grad: 'from-cyan-500/20 to-transparent' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', bar: 'bg-emerald-400', border: 'border-emerald-500/30', grad: 'from-emerald-500/20 to-transparent' },
  purple:  { text: 'text-purple-400',  bg: 'bg-purple-500/10',  bar: 'bg-purple-400',  border: 'border-purple-500/30',  grad: 'from-purple-500/20 to-transparent' },
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-500/10',   bar: 'bg-amber-400',   border: 'border-amber-500/30',   grad: 'from-amber-500/20 to-transparent' },
  rose:    { text: 'text-rose-400',    bg: 'bg-rose-500/10',    bar: 'bg-rose-400',    border: 'border-rose-500/30',    grad: 'from-rose-500/20 to-transparent' },
  gray:    { text: 'text-gray-300',    bg: 'bg-white/[0.03]',   bar: 'bg-gray-400',    border: 'border-white/10',       grad: 'from-white/[0.06] to-transparent' },
};

interface Analytics {
  ok: boolean;
  generated_at: string;
  summary: {
    posted_30d: number;
    skipped_30d: number;
    approval_rate_pct: number | null;
    cost_usd_30d: number;
    replies_today: number;
    followups_today: number;
    max_replies_per_day: number | null;
    max_followups_per_day: number | null;
  };
  followers: {
    latest: number | null;
    delta_7d: number | null;
    delta_30d: number | null;
    series: { day: string; count: number }[];
  };
  daily_rollup: {
    day: string; initial_posted: number; initial_skipped: number;
    followup_posted: number; followup_skipped: number;
    total_posted: number; total_cost_usd: number;
  }[];
  hashtag_perf: {
    hashtag: string; category: string; weight: number;
    replies_sent: number; approved_count: number; skipped_count: number;
    approval_rate_pct: number | null; avg_judgment_score: number | null;
  }[];
  hourly_engagement: {
    hour_of_day_utc: number; sample_size: number;
    avg_likes: number; avg_replies: number; avg_retweets: number;
    avg_engagement_score: number;
  }[];
  recent_posts: {
    tweet_id: string; posted_at: string; source: string; tweet_text: string;
    likes: number; replies: number; retweets: number; impressions: number | null;
  }[];
  viral_pipeline: {
    discovered: number; pushed: number; approved: number; skipped: number; expired: number;
  };
  thread_pipeline: {
    draft: number; approved: number; posted: number; failed: number;
    next_scheduled: any;
  };
  attribution: {
    twitter_totals: { clicks: number; signups: number; conversion_rate_pct: number | null };
    by_source_campaign: {
      utm_source: string; utm_campaign: string;
      clicks: number; signups: number; conversion_rate_pct: number | null;
      first_seen: string; last_seen: string;
    }[];
    daily: { day: string; utm_source: string; clicks: number; signups: number }[];
  };
  tweet_queue: {
    by_category: {
      content_category: string; pending_count: number; posted_count: number;
      failed_count: number; last_posted_at: string | null;
    }[];
    total_pending: number;
    total_posted: number;
    total_failed: number;
    projected_schedule?: {
      next_slot_at: string | null;
      queue_empty_at: string | null;
      peak_hours_utc: number[];
      config: {
        max_per_day: number;
        min_gap_min: number;
        peak_top_n: number;
        bootstrap: boolean;
      };
      upcoming_slots: string[];
    };
  };
  autoplug: {
    candidates: {
      tweet_id: string; posted_at: string; source: string; tweet_text: string;
      likes: number; replies: number; retweets: number;
      auto_plug_replied: boolean; auto_plug_tweet_id: string | null; auto_plug_at: string | null;
    }[];
    pending_count: number;
    plugged_count: number;
  };
  config: Record<string, any>;
}

const Kpi = ({ icon: Icon, label, value, sub, color = 'cyan', trend }: any) => {
  const a = ACCENT[color] || ACCENT.cyan;
  const trendIcon = trend > 0 ? FiTrendingUp : trend < 0 ? FiTrendingDown : null;
  const trendColor = trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-rose-400' : 'text-gray-500';
  return (
    <div className={`rounded-2xl border ${a.border} ${a.bg} p-4 relative overflow-hidden`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${a.grad} pointer-events-none opacity-60`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-3.5 h-3.5 ${a.text}`} />
            <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${a.text}`}>
              {label}
            </span>
          </div>
          {trendIcon && (
            <div className={`flex items-center gap-1 text-xs font-mono ${trendColor}`}>
              {React.createElement(trendIcon, { className: 'w-3 h-3' })}
              <span>{trend > 0 ? '+' : ''}{trend}</span>
            </div>
          )}
        </div>
        <div className="text-3xl font-black text-white tabular-nums font-mono leading-none">{value}</div>
        {sub && <div className="text-[11px] text-gray-500 mt-2 font-mono">{sub}</div>}
      </div>
    </div>
  );
};

const Bars = ({ data, color = 'cyan', valueKey = 'count', labelKey = 'day', height = 128 }: any) => {
  const max = Math.max(1, ...data.map((d: any) => d[valueKey]));
  const a = ACCENT[color] || ACCENT.cyan;
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.length === 0 ? (
        <div className="text-xs text-gray-500 font-mono">No data yet.</div>
      ) : (
        data.map((d: any, i: number) => {
          const h = Math.max(2, Math.round((d[valueKey] / max) * 100));
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div
                className={`w-full ${a.bar} rounded-t-sm transition-opacity group-hover:opacity-100 opacity-70`}
                style={{ height: `${h}%` }}
                title={`${d[labelKey]}: ${d[valueKey]}`}
              />
              {i % Math.max(1, Math.floor(data.length / 8)) === 0 && (
                <span className="text-[9px] text-gray-600 font-mono whitespace-nowrap">
                  {String(d[labelKey]).slice(5, 10)}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

// Simple line chart (SVG) — used for follower growth so the visual is
// clearly "trend over time" rather than "count per bucket".
const LineSpark = ({ series, color = 'emerald', height = 96 }: any) => {
  const a = ACCENT[color] || ACCENT.emerald;
  if (!series || series.length < 2) {
    return <div className="text-xs text-gray-500 font-mono">Need 2+ snapshots to chart.</div>;
  }
  const w = 400;
  const h = height;
  const values = series.map((s: any) => Number(s.count) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const step = w / Math.max(1, series.length - 1);
  const points = values
    .map((v: number, i: number) => `${i * step},${h - ((v - min) / range) * (h - 8) - 4}`)
    .join(' ');
  const strokeColor = color === 'emerald' ? '#34d399' : color === 'cyan' ? '#22d3ee' : '#a78bfa';
  const fillColor = color === 'emerald'   ? 'rgba(52,211,153,0.15)' :
                    color === 'cyan'      ? 'rgba(34,211,238,0.15)' :
                                            'rgba(167,139,250,0.15)';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none">
      <polygon points={`0,${h} ${points} ${w},${h}`} fill={fillColor} />
      <polyline points={points} fill="none" stroke={strokeColor} strokeWidth="1.5" />
    </svg>
  );
};

const HourlyHeatmap = ({ hourly }: { hourly: Analytics['hourly_engagement'] }) => {
  const byHour = new Map(hourly.map(h => [h.hour_of_day_utc, h]));
  const maxScore = Math.max(1, ...hourly.map(h => Number(h.avg_engagement_score) || 0));
  return (
    <div className="grid grid-cols-12 gap-1">
      {Array.from({ length: 24 }, (_, hour) => {
        const row = byHour.get(hour);
        const score = row ? Number(row.avg_engagement_score) : 0;
        const intensity = score / maxScore;
        const bg = intensity === 0
          ? 'bg-white/[0.02] border-white/[0.05]'
          : intensity < 0.33
            ? 'bg-cyan-500/10 border-cyan-500/20'
            : intensity < 0.66
              ? 'bg-cyan-500/30 border-cyan-500/50'
              : 'bg-cyan-500/60 border-cyan-400/70';
        return (
          <div
            key={hour}
            className={`aspect-square rounded border ${bg} flex flex-col items-center justify-center relative group cursor-help`}
            title={row ? `${hour}h UTC — ${row.sample_size} tweets, score ${score.toFixed(1)}` : `${hour}h UTC — no data`}
          >
            <span className="text-[9px] font-mono text-gray-400">{hour}</span>
            {row && row.sample_size > 0 && (
              <span className="text-[10px] font-black text-white tabular-nums">{Math.round(score)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

type Tab = 'overview' | 'queue' | 'threads' | 'raw';

const TwitterGrowthPanel: React.FC = () => {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Not logged in');
      const res = await fetch('/api/growth-api?action=analytics', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setData(json);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Growth momentum — compare last-7d posted vs previous-7d.
  const momentum = useMemo(() => {
    if (!data) return null;
    const rows = data.daily_rollup || [];
    if (rows.length < 14) return null;
    const last7 = rows.slice(-7).reduce((s, r) => s + Number(r.total_posted || 0), 0);
    const prev7 = rows.slice(-14, -7).reduce((s, r) => s + Number(r.total_posted || 0), 0);
    return { last7, prev7, delta: last7 - prev7 };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <FiRefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-6 max-w-xl">
        <div className="flex items-center gap-2 text-rose-300 font-semibold mb-1">
          <FiXCircle /> Analytics failed
        </div>
        <div className="text-sm text-rose-200 font-mono">{err}</div>
        <button
          onClick={load}
          className="mt-4 px-3 py-1.5 rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-200 text-xs font-mono hover:bg-rose-500/30"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FiActivity className="text-cyan-400" />
            Twitter Growth
          </h2>
          <p className="text-sm text-gray-500 mt-1 font-mono">
            Last refresh: {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-white/10 p-0.5 bg-white/[0.02]">
            {(['overview', 'queue', 'threads', 'raw'] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm ${
                  tab === t ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-400 hover:text-white'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/10 text-slate-300 text-xs font-mono hover:bg-white/[0.08]"
          >
            <FiRefreshCw className="inline w-3 h-3 mr-1" /> Refresh
          </button>
        </div>
      </div>

      {tab === 'overview' && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi
              icon={FiUsers}
              label="Followers"
              value={data.followers.latest != null ? data.followers.latest.toLocaleString() : '—'}
              sub={
                data.followers.delta_7d != null
                  ? `${data.followers.delta_7d >= 0 ? '+' : ''}${data.followers.delta_7d} last 7d`
                  : 'Snapshot cron pending'
              }
              color="emerald"
              trend={data.followers.delta_7d}
            />
            <Kpi
              icon={FiCheckCircle}
              label="Posted 30d"
              value={data.summary.posted_30d}
              sub={
                data.summary.approval_rate_pct != null
                  ? `${data.summary.approval_rate_pct}% approval rate`
                  : 'Approve some to compute rate'
              }
              color="cyan"
            />
            <Kpi
              icon={FiZap}
              label="Today"
              value={`${data.summary.replies_today + data.summary.followups_today}`}
              sub={
                `${data.summary.replies_today}/${data.summary.max_replies_per_day ?? '?'} initial · ` +
                `${data.summary.followups_today}/${data.summary.max_followups_per_day ?? '?'} follow-up`
              }
              color="amber"
            />
            <Kpi
              icon={FiDollarSign}
              label="Cost 30d"
              value={`$${data.summary.cost_usd_30d.toFixed(2)}`}
              sub="Anthropic Fable 5 (judge + generate)"
              color="purple"
            />
          </div>

          {/* Attribution — Twitter → signups conversion */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi
              icon={FiTarget}
              label="Twitter clicks 90d"
              value={data.attribution?.twitter_totals?.clicks ?? 0}
              sub="From ?utm_source=twitter links"
              color="cyan"
            />
            <Kpi
              icon={FiCheckCircle}
              label="Twitter signups 90d"
              value={data.attribution?.twitter_totals?.signups ?? 0}
              sub={
                data.attribution?.twitter_totals?.conversion_rate_pct != null
                  ? `${data.attribution.twitter_totals.conversion_rate_pct}% conversion`
                  : 'No signups yet'
              }
              color="emerald"
            />
            <Kpi
              icon={FiActivity}
              label="Total sources 90d"
              value={(data.attribution?.by_source_campaign || []).length}
              sub="Distinct (source, campaign) pairs"
              color="amber"
            />
            <Kpi
              icon={FiEye}
              label="All clicks 90d"
              value={(data.attribution?.by_source_campaign || []).reduce((s, r) => s + Number(r.clicks || 0), 0)}
              sub="Across all sources"
              color="purple"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center gap-2 mb-4">
              <FiTarget className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400">
                Attribution — clicks & signups by source
              </span>
            </div>
            {(data.attribution?.by_source_campaign || []).length === 0 ? (
              <div className="text-xs text-gray-500 font-mono py-6 text-center">
                No inbound tracked yet — as soon as someone lands on
                snap-chart.ai/?ref=E866SU&utm_source=twitter&utm_campaign=verdict
                this table populates.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-gray-500 border-b border-white/10">
                      <th className="text-left  py-2 px-2">Source</th>
                      <th className="text-left  py-2 px-2">Campaign</th>
                      <th className="text-right py-2 px-2">Clicks</th>
                      <th className="text-right py-2 px-2">Signups</th>
                      <th className="text-right py-2 px-2">Conv %</th>
                      <th className="text-right py-2 px-2">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.attribution?.by_source_campaign || []).map((r, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-1.5 px-2">
                          <span className={sourceBadge(r.utm_source)}>{r.utm_source}</span>
                        </td>
                        <td className="py-1.5 px-2 text-slate-300">{r.utm_campaign}</td>
                        <td className="py-1.5 px-2 text-right text-white tabular-nums">{r.clicks}</td>
                        <td className="py-1.5 px-2 text-right text-emerald-400 tabular-nums">{r.signups}</td>
                        <td className="py-1.5 px-2 text-right text-slate-300 tabular-nums">
                          {r.conversion_rate_pct != null ? `${r.conversion_rate_pct}%` : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right text-gray-500 text-[10px]">
                          {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Follower trend + Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FiTrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400">
                    Follower Growth
                  </span>
                </div>
                {momentum && (
                  <span className={`text-xs font-mono ${momentum.delta > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    Momentum: {momentum.delta > 0 ? '+' : ''}{momentum.delta} vs prev 7d
                  </span>
                )}
              </div>
              <LineSpark series={data.followers.series} color="emerald" height={120} />
              <div className="mt-3 flex justify-between text-[10px] text-gray-500 font-mono">
                <span>
                  {data.followers.series[0]?.day
                    ? `Since ${new Date(data.followers.series[0].day).toLocaleDateString()}`
                    : 'No snapshots yet'}
                </span>
                <span>{data.followers.series.length} snapshots</span>
              </div>
            </div>

            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.03] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FiActivity className="w-4 h-4 text-cyan-400" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
                    Daily activity (30d)
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono">
                  <span className="flex items-center gap-1 text-cyan-400">
                    <span className="w-2 h-2 rounded-sm bg-cyan-400" /> Initial
                  </span>
                  <span className="flex items-center gap-1 text-purple-400">
                    <span className="w-2 h-2 rounded-sm bg-purple-400" /> Follow-up
                  </span>
                </div>
              </div>
              <div className="flex items-end gap-1 h-32">
                {(data.daily_rollup || []).map((d: any, i: number) => {
                  const max = Math.max(1, ...data.daily_rollup.map(r => r.total_posted));
                  const iH = Math.round((d.initial_posted / max) * 100);
                  const fH = Math.round((d.followup_posted / max) * 100);
                  return (
                    <div key={i} className="flex-1 flex flex-col-reverse gap-0.5 relative group">
                      <div
                        className="w-full bg-cyan-400 rounded-t-sm opacity-70 group-hover:opacity-100"
                        style={{ height: `${iH}%` }}
                        title={`${d.day}: ${d.initial_posted} initial + ${d.followup_posted} follow-up`}
                      />
                      <div
                        className="w-full bg-purple-400 opacity-70 group-hover:opacity-100"
                        style={{ height: `${fH}%` }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Best time to post + Viral pipeline */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.03] p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FiClock className="w-4 h-4 text-amber-400" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">
                    Best time to post (UTC)
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-500">
                  {data.hourly_engagement.length === 0
                    ? 'Waiting for >= 24h-old tweets'
                    : `${data.hourly_engagement.reduce((s, h) => s + h.sample_size, 0)} settled tweets`}
                </span>
              </div>
              <HourlyHeatmap hourly={data.hourly_engagement} />
              <p className="mt-3 text-[10px] font-mono text-gray-500 leading-relaxed">
                Score = avg(likes + replies×2 + retweets×3) over your posted tweets grouped by UTC hour.
                Higher intensity = post more at that hour. Sample size shown on hover.
              </p>
            </div>

            <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.03] p-5">
              <div className="flex items-center gap-2 mb-3">
                <FiEye className="w-4 h-4 text-rose-400" />
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-rose-400">
                  Viral pipeline (30d)
                </span>
              </div>
              <div className="space-y-2 font-mono text-sm">
                <PipeRow label="Discovered" value={data.viral_pipeline.discovered} color="rose" />
                <PipeRow label="Pushed to TG" value={data.viral_pipeline.pushed} color="amber" />
                <PipeRow label="Approved" value={data.viral_pipeline.approved} color="emerald" />
                <PipeRow label="Skipped" value={data.viral_pipeline.skipped} color="gray" />
                <PipeRow label="Expired" value={data.viral_pipeline.expired} color="gray" />
              </div>
              <p className="mt-3 text-[10px] font-mono text-gray-500 leading-relaxed">
                Viral finder scans hashtags every 2h for tweets with {data.config.viral_min_likes ?? 50}+ likes
                and less than {data.config.viral_max_replies ?? 200} replies.
              </p>
            </div>
          </div>

          {/* Auto-plug candidates — YOUR viral tweets that will be plugged */}
          {(data.autoplug?.candidates || []).length > 0 && (
            <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.03] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FiZap className="w-4 h-4 text-rose-400" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-rose-400">
                    Auto-plug candidates
                  </span>
                </div>
                <div className="text-[10px] font-mono">
                  <span className="text-amber-400">{data.autoplug?.pending_count || 0} pending</span>
                  {' · '}
                  <span className="text-emerald-400">{data.autoplug?.plugged_count || 0} plugged</span>
                </div>
              </div>
              <div className="space-y-2">
                {(data.autoplug?.candidates || []).slice(0, 5).map((c: any) => (
                  <div key={c.tweet_id} className="rounded-lg border border-white/10 bg-black/30 p-2.5 text-xs flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {c.auto_plug_replied ? (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                            plugged
                          </span>
                        ) : (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
                            eligible
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-gray-500">
                          {new Date(c.posted_at).toLocaleDateString()} · {c.source}
                        </span>
                      </div>
                      <div className="text-slate-300 line-clamp-2">{c.tweet_text}</div>
                    </div>
                    <div className="flex-shrink-0 text-right space-y-0.5">
                      <div className="text-lg font-black text-rose-400 tabular-nums">{c.likes}</div>
                      <div className="text-[9px] font-mono text-gray-500">♥ likes</div>
                      <a href={`https://x.com/i/status/${c.tweet_id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-mono text-cyan-400 hover:text-cyan-300 block">view</a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[10px] font-mono text-gray-500">
                Auto-plug cron replies to eligible tweets every 30 min. Threshold: {data.config?.autoplug_min_likes ?? 50} likes,
                {' '}{data.config?.autoplug_min_hours_since_post ?? 4}h old, max {data.config?.autoplug_max_per_day ?? 3}/day.
              </div>
            </div>
          )}

          {/* Hashtag performance */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center gap-2 mb-4">
              <FiHash className="w-4 h-4 text-cyan-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
                Hashtag performance (top 15)
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-gray-500 border-b border-white/10">
                    <th className="text-left py-2 px-2">Hashtag</th>
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-right py-2 px-2">Approved</th>
                    <th className="text-right py-2 px-2">Skipped</th>
                    <th className="text-right py-2 px-2">Approval %</th>
                    <th className="text-right py-2 px-2">Avg Score</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.hashtag_perf || []).slice(0, 15).map((h: any, i: number) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-1.5 px-2 text-cyan-300">#{h.hashtag}</td>
                      <td className="py-1.5 px-2 text-gray-400">{h.category}</td>
                      <td className="py-1.5 px-2 text-right text-emerald-400">{h.approved_count}</td>
                      <td className="py-1.5 px-2 text-right text-gray-500">{h.skipped_count}</td>
                      <td className="py-1.5 px-2 text-right text-white">
                        {h.approval_rate_pct != null ? `${h.approval_rate_pct}%` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right text-gray-300">
                        {h.avg_judgment_score != null ? Number(h.avg_judgment_score).toFixed(1) : '—'}
                      </td>
                    </tr>
                  ))}
                  {(data.hashtag_perf || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-gray-500">
                        No hashtag activity yet — approve a few replies to seed this table.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent posts */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center gap-2 mb-4">
              <FiTarget className="w-4 h-4 text-purple-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-purple-400">
                Recent posts — engagement snapshot
              </span>
            </div>
            {(data.recent_posts || []).length === 0 ? (
              <div className="text-xs text-gray-500 font-mono py-4">
                No posts tracked yet — /api/tweet-engagement-sync cron will populate this.
              </div>
            ) : (
              <div className="space-y-2">
                {(data.recent_posts || []).map((p: any) => (
                  <a
                    key={p.tweet_id}
                    href={`https://x.com/i/status/${p.tweet_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg border border-white/10 hover:border-cyan-500/40 bg-white/[0.02] hover:bg-white/[0.04] p-3 text-xs transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={sourceBadge(p.source)}>
                        {p.source}
                      </span>
                      <span className="font-mono text-[10px] text-gray-500">
                        {new Date(p.posted_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-slate-300 line-clamp-2 mb-2">{p.tweet_text}</div>
                    <div className="flex items-center gap-4 text-[11px] font-mono text-gray-400">
                      <span>♥ {p.likes}</span>
                      <span>💬 {p.replies}</span>
                      <span>↻ {p.retweets}</span>
                      {p.impressions != null && <span>👁 {p.impressions}</span>}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'queue' && <ContentQueueManager stats={data.tweet_queue} onRefresh={load} />}

      {tab === 'threads' && <ThreadComposer onSaved={load} threadPipeline={data.thread_pipeline} />}

      {tab === 'raw' && (
        <pre className="rounded-lg bg-black/40 border border-white/10 p-4 text-[11px] font-mono text-slate-300 overflow-auto max-h-[70vh]">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

const PipeRow = ({ label, value, color }: any) => {
  const a = ACCENT[color] || ACCENT.gray;
  return (
    <div className="flex items-center justify-between">
      <span className={`${a.text}`}>{label}</span>
      <span className="text-white tabular-nums">{value}</span>
    </div>
  );
};

// Shared badge — used for both post sources (analysis/daily/reply/thread)
// AND utm_source labels (twitter/telegram/direct/email). No overlap in
// the string set so a single switch is fine.
const sourceBadge = (src: string) => {
  const base = 'text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded inline-block';
  switch (src) {
    // Post sources
    case 'analysis': return `${base} bg-cyan-500/20 text-cyan-300 border border-cyan-500/40`;
    case 'daily':    return `${base} bg-amber-500/20 text-amber-300 border border-amber-500/40`;
    case 'reply':    return `${base} bg-emerald-500/20 text-emerald-300 border border-emerald-500/40`;
    case 'followup': return `${base} bg-purple-500/20 text-purple-300 border border-purple-500/40`;
    case 'thread':   return `${base} bg-rose-500/20 text-rose-300 border border-rose-500/40`;
    // UTM sources
    case 'twitter':  return `${base} bg-sky-500/20 text-sky-300 border border-sky-500/40`;
    case 'telegram': return `${base} bg-cyan-500/20 text-cyan-300 border border-cyan-500/40`;
    case 'email':    return `${base} bg-amber-500/20 text-amber-300 border border-amber-500/40`;
    case '(direct)': return `${base} bg-white/10 text-gray-300 border border-white/20`;
    default:         return `${base} bg-white/10 text-gray-300 border border-white/20`;
  }
};

export default TwitterGrowthPanel;
